import { Box, SvgIcon, Typography } from '@mui/material';
import { FC } from 'react';
import DocumentIcon from '../../../../../icons/svg/document.svg?react';
import { ActionOverlay } from './actionOverlay';

export type FilePreviewProps = {
  name: string;
  size: number;
  preview: string;
  type?: string;
  onDelete: () => void;
  disableDelete?: boolean;
  hideFileInfo?: boolean;
  width?: string;
  height?: string;
  dataTestId?: string;
};

export const FilePreview: FC<FilePreviewProps> = ({
  name,
  size,
  preview,
  type,
  onDelete,
  disableDelete = false,
  hideFileInfo = false,
  width = undefined,
  height = undefined,
  dataTestId = undefined
}) => {
  const isImage = type && type.startsWith('image/');

  return (
    <Box
      sx={(theme) => ({
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        width: width || '200px',
        height: (height ? `${parseInt(height) + 40}px` : undefined) || '120px',
        borderRadius: '8px',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: theme.palette.grey[50],
        backgroundColor: 'white',
        overflow: 'hidden',
        '&:hover > .file-upload-overlay': {
          display: 'block'
        }
      })}
      data-testid={dataTestId}
    >
      {!disableDelete && (
        <ActionOverlay
          onDelete={onDelete}
          width={width}
          height={height ? `${parseInt(height) + (hideFileInfo ? 55 : 0)}px` : undefined}
        />
      )}
      {isImage ? (
        <ImagePreview preview={preview} width={width} height={height} hideFileInfo={hideFileInfo} />
      ) : (
        <DocumentPreview width={width} height={height} />
      )}
      {!hideFileInfo && (
        <Box
          sx={(theme) => ({
            height: '50px',
            padding: '0px 10px 0px',
            backgroundColor: theme.palette.grey[50]
          })}
        >
          <Typography noWrap fontWeight="bold" fontSize="0.75rem" sx={{ marginBottom: '-2px' }}>
            {name}
          </Typography>
          <Typography noWrap fontSize="0.625rem" display={size ? 'inline-block' : 'none'}>
            {size / 1000} KB
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const ImagePreview: FC<{
  preview: string;
  width?: string;
  height?: string;
  hideFileInfo?: boolean;
}> = ({ preview, width = undefined, height = undefined, hideFileInfo = false }) => (
  <Box
    sx={{
      background: `url(${preview})`,
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      height: (height ? `${parseInt(height) + (hideFileInfo ? 40 : -10)}px` : undefined) || '70px',
      width: width || '200px'
    }}
  />
);

const DocumentPreview: FC<{ width?: string; height?: string }> = ({ width, height }) => {
  return (
    <Box
      sx={{
        height: height || '70px',
        width: width || '200px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <SvgIcon component={DocumentIcon} />
    </Box>
  );
};
